import React, { useState, useEffect, useCallback } from 'react';
import { useToast } from '../../contexts/ToastContext';
import { tiposAPI } from '../../services/api';
import api from '../../services/api';
import './TiposYPartesConfig.css';

const asArray = (v) => (Array.isArray(v) ? v : []);

const TiposYPartesConfig = () => {
  const { showSuccess, showError, showConfirm } = useToast();
  const [tipos, setTipos] = useState([]);
  const [partes, setPartes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingTipo, setEditingTipo] = useState(null);
  const [tipoForm, setTipoForm] = useState({ nombre: '', descripcion: '' });
  const [saving, setSaving] = useState(false);

  const [newParteText, setNewParteText] = useState('');
  const [editingParteId, setEditingParteId] = useState(null);
  const [editingParteText, setEditingParteText] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const [tiposRes, partesRes] = await Promise.all([
        tiposAPI.getTipos(),
        api.get('/partes-preestablecidas')
      ]);
      setTipos(asArray(tiposRes?.data));
      setPartes(asArray(partesRes?.data));
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Error desconocido';
      setLoadError(msg);
      setTipos([]);
      setPartes([]);
      showError('Error al cargar: ' + msg);
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getPartesByTipo = (tipoId) =>
    partes
      .filter((p) => p.tipoId === tipoId)
      .sort((a, b) => (a.orden ?? 999) - (b.orden ?? 999));

  const openModal = (tipo = null) => {
    setEditingTipo(tipo);
    setTipoForm({
      nombre: tipo?.nombre || '',
      descripcion: tipo?.descripcion || ''
    });
    setNewParteText('');
    setEditingParteId(null);
    setEditingParteText('');
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingTipo(null);
    setTipoForm({ nombre: '', descripcion: '' });
    setNewParteText('');
    setEditingParteId(null);
  };

  const handleSaveTipo = async () => {
    if (!tipoForm.nombre.trim()) {
      showError('El nombre del tipo es obligatorio');
      return;
    }
    setSaving(true);
    try {
      if (editingTipo?.id) {
        const id = editingTipo.id;
        await tiposAPI.updateTipo(id, tipoForm);
        showSuccess('Tipo actualizado');
        await loadData();
        setEditingTipo((prev) => ({ ...prev, id, ...tipoForm }));
      } else {
        const res = await tiposAPI.createTipo(tipoForm);
        const created = res?.data;
        showSuccess('Tipo creado — ya puedes agregar partes');
        await loadData();
        if (created?.id) {
          setEditingTipo(created);
          setTipoForm({
            nombre: created.nombre || tipoForm.nombre,
            descripcion: created.descripcion ?? tipoForm.descripcion
          });
        }
      }
    } catch (err) {
      showError('Error al guardar tipo: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTipo = async (tipo, e) => {
    e?.stopPropagation();
    if ((tipo._count?.expediente || 0) > 0) {
      showError('No se puede eliminar: tiene expedientes asociados');
      return;
    }
    if (!await showConfirm('¿Eliminar este tipo y su plantilla de partes?')) return;
    try {
      await tiposAPI.deleteTipo(tipo.id);
      showSuccess('Tipo eliminado');
      if (editingTipo?.id === tipo.id) closeModal();
      loadData();
    } catch (err) {
      showError('Error al eliminar: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleAddParte = async () => {
    const descripcion = newParteText.trim();
    if (!descripcion) return;
    if (!editingTipo?.id) {
      showError('Guarda el tipo primero para agregar partes');
      return;
    }
    try {
      await api.post('/partes-preestablecidas', {
        tipoId: editingTipo.id,
        descripcion,
        orden: null
      });
      showSuccess('Parte agregada');
      setNewParteText('');
      await loadData();
    } catch (err) {
      showError('Error al agregar parte: ' + (err.response?.data?.error || err.message));
    }
  };

  const startEditParte = (parte) => {
    setEditingParteId(parte.id);
    setEditingParteText(parte.descripcion);
  };

  const cancelEditParte = () => {
    setEditingParteId(null);
    setEditingParteText('');
  };

  const saveEditParte = async (parte) => {
    const descripcion = editingParteText.trim();
    if (!descripcion) {
      showError('La descripción no puede estar vacía');
      return;
    }
    try {
      await api.put(`/partes-preestablecidas/${parte.id}`, {
        descripcion,
        orden: parte.orden ?? null
      });
      showSuccess('Parte actualizada');
      cancelEditParte();
      await loadData();
    } catch (err) {
      showError('Error al actualizar: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDeleteParte = async (parteId) => {
    if (!await showConfirm('¿Quitar esta parte de la plantilla?')) return;
    try {
      await api.delete(`/partes-preestablecidas/${parteId}`);
      showSuccess('Parte desactivada');
      if (editingParteId === parteId) cancelEditParte();
      await loadData();
    } catch (err) {
      showError('Error al eliminar parte: ' + (err.response?.data?.error || err.message));
    }
  };

  const modalPartes = editingTipo?.id ? getPartesByTipo(editingTipo.id) : [];
  const tipoEnModal = editingTipo?.id
    ? tipos.find((t) => t.id === editingTipo.id) || editingTipo
    : editingTipo;

  if (loading) {
    return (
      <div className="tyc-page">
        <div className="tyc-loading">
          <div className="spinner-border text-primary" role="status" />
          <p className="mt-3 mb-0">Cargando tipos y partes…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="tyc-page">
      <div className="tyc-page-header">
        <div>
          <h3 className="tyc-page-title">Tipos y partes</h3>
          <p className="tyc-page-sub">Plantilla para expedientes nuevos</p>
        </div>
        <button type="button" className="tyc-btn-main" onClick={() => openModal(null)}>
          + Nuevo tipo
        </button>
      </div>

      {loadError && (
        <div className="tyc-error-box">
          {loadError}
          <button type="button" className="tyc-btn-ghost primary" style={{ marginLeft: 8 }} onClick={loadData}>
            Reintentar
          </button>
        </div>
      )}

      {tipos.length === 0 ? (
        <div className="tyc-empty">
          <p className="mb-3">No hay tipos configurados</p>
          <button type="button" className="tyc-btn-main" onClick={() => openModal(null)}>
            Crear primer tipo
          </button>
        </div>
      ) : (
        <div className="tyc-grid">
          {tipos.map((tipo) => {
            const count = getPartesByTipo(tipo.id).length;
            return (
              <div key={tipo.id} className="tyc-tipo-card">
                <div>
                  <div className="tyc-tipo-name">{tipo.nombre}</div>
                  {tipo.descripcion && <div className="tyc-tipo-desc">{tipo.descripcion}</div>}
                  <div className="tyc-tipo-meta">
                    {tipo._count?.expediente ?? 0} expedientes · {count} partes en plantilla
                  </div>
                </div>
                <div className="tyc-card-actions">
                  <button type="button" className="tyc-btn-ghost primary" onClick={() => openModal(tipo)}>
                    Gestionar
                  </button>
                  <button
                    type="button"
                    className="tyc-btn-ghost danger"
                    disabled={(tipo._count?.expediente || 0) > 0}
                    onClick={(e) => handleDeleteTipo(tipo, e)}
                    title={(tipo._count?.expediente || 0) > 0 ? 'Tiene expedientes asociados' : ''}
                  >
                    Eliminar
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {modalOpen && (
        <div className="tyc-overlay" onClick={closeModal} role="presentation">
          <div
            className="tyc-dialog"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="tyc-modal-title"
          >
            <div className="tyc-dialog-header">
              <div>
                <h2 id="tyc-modal-title">
                  {editingTipo?.id ? `Gestionar: ${tipoEnModal?.nombre || 'Tipo'}` : 'Nuevo tipo de expediente'}
                </h2>
                <p>Edita el tipo y sus partes en un solo lugar</p>
              </div>
              <button type="button" className="tyc-dialog-close" onClick={closeModal} aria-label="Cerrar">
                ×
              </button>
            </div>

            <div className="tyc-dialog-body">
              <div className="tyc-field">
                <label htmlFor="tyc-nombre">Nombre del tipo *</label>
                <input
                  id="tyc-nombre"
                  type="text"
                  value={tipoForm.nombre}
                  onChange={(e) => setTipoForm({ ...tipoForm, nombre: e.target.value })}
                  placeholder="Ej. Plano de construcción"
                  autoFocus
                />
              </div>
              <div className="tyc-field">
                <label htmlFor="tyc-desc">Descripción</label>
                <input
                  id="tyc-desc"
                  type="text"
                  value={tipoForm.descripcion}
                  onChange={(e) => setTipoForm({ ...tipoForm, descripcion: e.target.value })}
                  placeholder="Opcional"
                />
              </div>

              <div className="tyc-section-title">Partes de la plantilla</div>

              {!editingTipo?.id ? (
                <p className="tyc-no-partes">
                  Guarda el tipo con el botón inferior para poder agregar partes.
                </p>
              ) : modalPartes.length === 0 ? (
                <p className="tyc-no-partes">Sin partes — agrega la primera abajo.</p>
              ) : (
                <ul className="tyc-partes-list">
                  {modalPartes.map((parte) => (
                    <li key={parte.id} className="tyc-parte-row">
                      <span className="tyc-parte-orden">{parte.orden ?? '—'}</span>
                      {editingParteId === parte.id ? (
                        <div className="tyc-parte-desc">
                          <input
                            type="text"
                            value={editingParteText}
                            onChange={(e) => setEditingParteText(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') saveEditParte(parte);
                              if (e.key === 'Escape') cancelEditParte();
                            }}
                            autoFocus
                          />
                        </div>
                      ) : (
                        <span className="tyc-parte-desc">{parte.descripcion}</span>
                      )}
                      <div className="tyc-parte-actions">
                        {editingParteId === parte.id ? (
                          <>
                            <button type="button" className="tyc-icon-btn" onClick={() => saveEditParte(parte)}>
                              OK
                            </button>
                            <button type="button" className="tyc-icon-btn" onClick={cancelEditParte}>
                              ✕
                            </button>
                          </>
                        ) : (
                          <>
                            <button type="button" className="tyc-icon-btn" onClick={() => startEditParte(parte)}>
                              Editar
                            </button>
                            <button
                              type="button"
                              className="tyc-icon-btn danger"
                              onClick={() => handleDeleteParte(parte.id)}
                            >
                              Quitar
                            </button>
                          </>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              {editingTipo?.id && (
                <div className="tyc-add-row">
                  <input
                    type="text"
                    placeholder="Nueva parte (Enter para agregar)"
                    value={newParteText}
                    onChange={(e) => setNewParteText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddParte();
                      }
                    }}
                  />
                  <button type="button" className="tyc-btn-main" onClick={handleAddParte}>
                    Agregar
                  </button>
                </div>
              )}
            </div>

            <div className="tyc-dialog-footer">
              <button type="button" className="tyc-btn-secondary" onClick={closeModal}>
                Cerrar
              </button>
              <button
                type="button"
                className="tyc-btn-main"
                onClick={handleSaveTipo}
                disabled={saving}
              >
                {saving ? 'Guardando…' : editingTipo?.id ? 'Guardar tipo' : 'Crear tipo'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TiposYPartesConfig;
